import { isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    PLATFORM_ID,
    WritableSignal,
    afterNextRender,
    inject,
    signal,
} from '@angular/core';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { PageHeaderComponent } from '@stair-craft/common/components';

const SECTION_IDS: readonly string[] = ['s1', 's2', 's3', 's4', 's5', 's6', 's7'];

/**
 * Window after a TOC click during which scroll-driven highlight updates are
 * suppressed — otherwise the smooth-scroll animation walks the highlight
 * through every section the camera passes, ending on the wrong one if the
 * landing isn't pixel-perfect.
 */
const TOC_CLICK_SUPPRESS_MS: number = 800;
const BEM_BLOCK: string = 'sc-terms-page';

@Component({
    selector: 'sc-terms-page',
    templateUrl: './terms-page.component.html',
    styleUrl: './terms-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        PageHeaderComponent,
    ],
    host: {
        class: BEM_BLOCK,
    },
})
export default class TermsPageComponent {
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #hostEl: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);

    #scrollHandler: (() => void) | null = null;
    #rafHandle: number | null = null;
    #suppressUntil: number = 0;

    protected readonly activeSectionId: WritableSignal<string> = signal<string>('s1');

    constructor() {
        afterNextRender((): void => {
            if (!isPlatformBrowser(this.#platformId)) {
                return;
            }
            const sections: HTMLElement[] = SECTION_IDS.map((id: string): HTMLElement | null =>
                this.#hostEl.nativeElement.querySelector<HTMLElement>(`#${id}`)
            ).filter((el: HTMLElement | null): el is HTMLElement => el !== null);
            if (sections.length === 0) {
                return;
            }

            const updateActive: () => void = (): void => {
                this.#rafHandle = null;
                if (Date.now() < this.#suppressUntil) {
                    return;
                }
                this.activeSectionId.set(this.#computeActiveId(sections));
            };

            this.#scrollHandler = (): void => {
                if (this.#rafHandle !== null) {
                    return;
                }
                this.#rafHandle = globalThis.requestAnimationFrame(updateActive);
            };

            updateActive();
            globalThis.addEventListener('scroll', this.#scrollHandler, { passive: true });
            globalThis.addEventListener('resize', this.#scrollHandler, { passive: true });
        });

        this.#destroyRef.onDestroy((): void => {
            if (this.#scrollHandler !== null) {
                globalThis.removeEventListener('scroll', this.#scrollHandler);
                globalThis.removeEventListener('resize', this.#scrollHandler);
            }
            if (this.#rafHandle !== null) {
                globalThis.cancelAnimationFrame(this.#rafHandle);
            }
        });
    }

    protected onTocClick(event: MouseEvent, sectionId: string): void {
        event.preventDefault();
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        const target: HTMLElement | null = this.#hostEl.nativeElement.querySelector<HTMLElement>(`#${sectionId}`);
        if (!target) {
            return;
        }
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.activeSectionId.set(sectionId);
        this.#suppressUntil = Date.now() + TOC_CLICK_SUPPRESS_MS;
        globalThis.history.replaceState(null, '', `#${sectionId}`);
    }

    /**
     * Active = the section whose heading (h2) is closest to but already at or
     * above the vertical center of the viewport. Tracks "what the user is
     * currently reading" — the section whose title has crossed the screen mid.
     *
     * If no heading has crossed center yet → first section.
     * If all headings have crossed → the last (lowest-positioned) one.
     */
    #computeActiveId(sections: readonly HTMLElement[]): string {
        const probeY: number = globalThis.innerHeight / 2;
        let bestId: string = sections[0].id;
        let bestTop: number = -Infinity;

        for (const section of sections) {
            const heading: HTMLElement | null = section.querySelector<HTMLElement>('h2');
            const top: number = (heading ?? section).getBoundingClientRect().top;
            if (top <= probeY && top > bestTop) {
                bestTop = top;
                bestId = section.id;
            }
        }
        return bestId;
    }
}
